import React from 'react';
import { useNavigate } from 'react-router-dom';
import LayoutHeader from '../../layout/LayoutHeader';

const SongwriterCreditsSetup = () => {
  const navigate = useNavigate();

  const setupOptions = [
    {
      id: 'upload-credits',
      title: 'Upload Songwriter Credits & Splits',
      subtitle: '(Publishing Side)',
      description: 'Populate your publishing catalog by uploading a CSV file from your PRO such as ASCAP, BMI, SESAC, SOCAN.',
      extendedDescription: 'This import includes song titles, writers, publishers, work IDs, and splits, providing the most complete and accurate view of your publishing data beyond basic registrations.',
      icon: (
        <svg className="w-5 h-5 text-[#00CED1]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 16.5V9.75m0 0l3 3m-3-3l-3 3M6.75 19.5a4.5 4.5 0 01-1.41-8.775 5.25 5.25 0 0110.233-2.33 3 3 0 013.758 3.848A3.752 3.752 0 0118 19.5H6.75z" />
        </svg>
      ),
      iconBg: 'rgba(0, 206, 209, 0.15)',
      action: () => navigate('/connectors/data-upload', { state: { type: 'label' } })
    },
    {
      id: 'auto-flow',
      title: 'Set up Auto Flow of Publishing data',
      subtitle: '(Real Time Sync)',
      description: 'As you write new songs, use Dizee Sheets to keep your publishing catalog automatically up to date.',
      extendedDescription: 'Simply convert your existing publishing spreadsheet into a Dizee Sheet and continue using Google Sheets exactly as you do today while new works, splits, and updates flow into Dizee in real time.',
      icon: (
        <svg className="w-5 h-5 text-[#FF6B35]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
        </svg>
      ),
      iconBg: 'rgba(255, 107, 53, 0.15)',
      action: () => navigate('/connectors/google-sheets')
    },
    {
      id: 'manual-add',
      title: 'Manually add new works via DIZEE AI or Works',
      subtitle: '(Alternative option)',
      description: "If you don't have publishing data in a spreadsheet and don't plan to use Dizee Sheets, you can add works one at a time.",
      extendedDescription: "Use the AI Chat to describe the song and splits in natural language or open the Works app and click 'Manual' to add.",
      icon: (
        <svg className="w-5 h-5 text-[#A78BFA]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
        </svg>
      ),
      iconBg: 'rgba(167, 139, 250, 0.15)',
      action: () => navigate('/catalog/works')
    }
  ];

  return (
    <LayoutHeader>
      <div className="flex justify-center w-full">
        <div className="w-[390px] h-[80vh] flex flex-col items-center relative z-[0]">
          {/* Header */}
          <div className="px-4 my-[50px] flex w-full justify-between items-center">
            <span className="text-white text-[14px]" style={{ fontWeight: 300 }}>Songwriter Credits</span>
            <span 
              className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70" 
              onClick={() => navigate('/data')}
            >
              Go back
            </span>
          </div>

          <div className="w-full px-4 pb-20 overflow-y-auto flex-1">
            {/* Page Title */}
            <div className="mb-2">
              <h1 className="text-white text-[20px] font-semibold">Songwriter Credits</h1>
              <p className="text-[rgba(255,255,255,0.5)] text-[13px]">(Publishing Side)</p>
            </div>

            {/* Description */}
            <div className="mb-8">
              <p className="text-[rgba(255,255,255,0.7)] text-[12px] leading-[1.6]">
                Pull your writing credits, compositions, and publishing splits into Dizee to complete a full picture of your catalog and ensure every work is properly tracked in one place.
              </p>
            </div>

            {/* Setup Options */}
            <div className="space-y-4">
              {setupOptions.map((option, index) => (
                <div
                  key={option.id}
                  onClick={option.action}
                  className="w-full bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.1)] rounded-xl p-4 hover:border-[rgba(255,255,255,0.25)] hover:bg-[rgba(255,255,255,0.05)] cursor-pointer transition-all duration-200"
                >
                  <div className="flex items-start gap-4">
                    {/* Icon */}
                    <div 
                      className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 border border-[rgba(255,255,255,0.1)]"
                      style={{ backgroundColor: option.iconBg }}
                    >
                      {option.icon}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <h3 className="text-white text-[13px] font-medium mb-1">
                        {option.title}
                      </h3>
                      {option.subtitle && (
                        <p className="text-[rgba(255,255,255,0.5)] text-[11px] mb-2">
                          {option.subtitle}
                        </p>
                      )}
                      <p className="text-[rgba(255,255,255,0.55)] text-[11px] leading-[1.5] mb-2">
                        {option.description}
                      </p>
                      {option.extendedDescription && (
                        <p className="text-[rgba(255,255,255,0.45)] text-[11px] leading-[1.5]">
                          {option.extendedDescription}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </LayoutHeader>
  );
};

export default SongwriterCreditsSetup;

